import React, { useState } from 'react';
import { DeviceCreate } from '../../api/models';
import { ClientWithDevices } from '../../types/client';
import { DeviceDetailModal } from '../devices/DeviceDetailModal';
import AuditHistoryTab from '../audit/AuditHistoryTab';
import { ClientUsersTab } from './ClientUsersTab';
import { useI18n } from '../../contexts/I18nContext';
import { hueOf } from '../users/UserAvatar';
import { clientInitials } from './clientInitials';
import { useClientDetail, ClientDetailTab } from './clientDetail/useClientDetail';
import { ClientOverviewTab } from './clientDetail/ClientOverviewTab';
import { ClientDevicesTab } from './clientDetail/ClientDevicesTab';

interface ClientDetailModalProps {
  client: ClientWithDevices | null;
  isOpen: boolean;
  onClose: () => void;
  onDeviceSelect?: (device: DeviceCreate) => void;
  onClientUpdated?: () => void;
  /** Every client in scope: the dialog finds the root and the agencies of this one there. */
  siblings?: ClientWithDevices[];
  /** Opens another client's dialog (an agency, the root). */
  onOpenClient?: (client: ClientWithDevices) => void;
  canAssignDevices?: boolean;
}

/**
 * One client: identity (name, SIREN, status), its fleet, its users and
 * its history. The state and the actions live in `useClientDetail`;
 * each tab is its own component.
 */
export const ClientDetailModal: React.FC<ClientDetailModalProps> = ({ client, isOpen, onClose, onDeviceSelect, onClientUpdated, siblings, onOpenClient, canAssignDevices }) => {
  const { formatNumber, t } = useI18n();
  const state = useClientDetail(client, isOpen, onClientUpdated, siblings);
  const { activeTab, setActiveTab, isEditing, isSaving, error } = state;
  const [selectedDevice, setSelectedDevice] = useState<DeviceCreate | null>(null);

  if (!isOpen || !client || !client.id) return null;

  const openDevice = (device: DeviceCreate) => {
    setSelectedDevice(device);
    onDeviceSelect?.(device);
  };

  const accessOn = client.provisioning_access ?? true;
  const tabs: Array<{ key: ClientDetailTab; label: string; testId: string; count?: number }> = [
    { key: 'overview', label: t('tabs.overview'), testId: 'tab-client-overview' },
    { key: 'devices', label: t('devices.devices'), testId: 'tab-client-devices', count: client.deviceCount },
    { key: 'users', label: t('clients.users.tabLabel'), testId: 'tab-client-users' },
    { key: 'history', label: t('audit.history.tabLabel'), testId: 'tab-client-history' },
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="config-template-detail-modal tpd cld" data-testid="modal-client-detail" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title tpd-title">
            <span className="tpd-mark cl-mark" style={{ ['--cl-hue' as any]: hueOf(client.id) }} aria-hidden="true">{clientInitials(client.name)}</span>
            <div className="tpd-title-text">
              <h2>{isEditing ? t('clients.detail.editTitle') : client.name}</h2>
              <div className="tpd-title-sub">
                <span className="mono">{client.id}</span>
                {!client.active && <span className="tpd-off">{t('common.inactive')}</span>}
                {!accessOn && <span className="tpd-off">{t('clients.detail.accessOff')}</span>}
              </div>
            </div>
          </div>
          <button type="button" className="close-button" data-testid="btn-modal-close" onClick={onClose} aria-label={t('common.close')}>×</button>
        </div>

        <div className="modal-tabs" role="tablist">
          {tabs.map((tab) => (
            <button key={tab.key} type="button" role="tab" aria-selected={activeTab === tab.key} className={`tab-button ${activeTab === tab.key ? 'active' : ''}`} data-testid={tab.testId} onClick={() => setActiveTab(tab.key)}>
              {tab.label}{tab.count !== undefined && <span className="ud-tab-count">{formatNumber(tab.count)}</span>}
            </button>
          ))}
        </div>

        <div className="modal-content tpd-body">
          {error && <div className="error-message" role="alert">{error}</div>}

          {activeTab === 'overview' && <ClientOverviewTab state={state} onOpenClient={onOpenClient} canAssignDevices={canAssignDevices} />}
          {activeTab === 'devices' && <ClientDevicesTab state={state} onOpenDevice={openDevice} />}
          {activeTab === 'users' && <ClientUsersTab clientId={client.id} clientName={client.name} clientAccess={accessOn} />}
          {activeTab === 'history' && <AuditHistoryTab resourceType="client" resourceId={client.id} />}
        </div>

        <div className="modal-actions tpd-footer">
          {isEditing ? (
            <>
              <button type="button" onClick={state.cancelEdit} data-testid="btn-client-cancel" className="btn btn-secondary" disabled={isSaving}>
                {t('common.cancel')}
              </button>
              <button type="button" onClick={state.save} data-testid="btn-client-save" className="btn btn-primary" disabled={!state.canSave}>
                {isSaving ? t('common.saving') : t('common.save')}
              </button>
            </>
          ) : (
            <>
              <button type="button" onClick={onClose} data-testid="btn-client-close" className="btn btn-secondary">
                {t('common.close')}
              </button>
              {activeTab === 'overview' && (
                <button type="button" onClick={() => state.setIsEditing(true)} data-testid="btn-client-edit" className="btn btn-primary">
                  {t('common.edit')}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {selectedDevice && (
        <DeviceDetailModal
          device={selectedDevice}
          isOpen
          onClose={() => setSelectedDevice(null)}
          onDeviceUpdated={() => { state.fetchDevices(); }}
        />
      )}
    </div>
  );
};
